import React, { useState, useEffect } from 'react';
import { LineChart, Line, AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import Icon from '../../../components/AppIcon';

const CampaignAnalyticsPanel = ({ selectedCampaign }) => {
  const [activeTab, setActiveTab] = useState('performance');
  const [timeRange, setTimeRange] = useState('7d');

  const performanceData = [
    { time: '00:00', calls: 12, revenue: 180.50, bids: 15 },
    { time: '04:00', calls: 8, revenue: 125.75, bids: 11 },
    { time: '08:00', calls: 24, revenue: 365.25, bids: 28 },
    { time: '12:00', calls: 35, revenue: 542.80, bids: 42 },
    { time: '16:00', calls: 28, revenue: 434.60, bids: 33 },
    { time: '20:00', calls: 18, revenue: 278.90, bids: 22 }
  ];

  const revenueData = [
    { date: '08/12', revenue: 1245.50, calls: 85, avgBid: 14.65 },
    { date: '08/13', revenue: 1389.75, calls: 92, avgBid: 15.10 },
    { date: '08/14', revenue: 1156.25, calls: 78, avgBid: 14.82 },
    { date: '08/15', revenue: 1567.80, calls: 105, avgBid: 14.93 },
    { date: '08/16', revenue: 1423.60, calls: 96, avgBid: 14.83 },
    { date: '08/17', revenue: 1678.90, calls: 112, avgBid: 14.99 },
    { date: '08/18', revenue: 1834.25, calls: 125, avgBid: 14.67 }
  ];

  const bidDistribution = [
    { range: '$10-12', count: 45, color: '#0EA5E9' },
    { range: '$12-14', count: 78, color: '#059669' },
    { range: '$14-16', count: 92, color: '#D97706' },
    { range: '$16-18', count: 34, color: '#DC2626' },
    { range: '$18+', count: 12, color: '#7C3AED' }
  ];

  const geographicData = [
    { state: 'CA', calls: 234, revenue: 3456.78, percentage: 28.5 },
    { state: 'TX', calls: 189, revenue: 2789.45, percentage: 23.1 },
    { state: 'FL', calls: 156, revenue: 2234.67, percentage: 19.0 },
    { state: 'NY', calls: 123, revenue: 1876.23, percentage: 15.0 },
    { state: 'Others', calls: 145, revenue: 2093.62, percentage: 14.4 }
  ];

  const tabs = [
    { id: 'performance', label: 'Performance', icon: 'TrendingUp' },
    { id: 'revenue', label: 'Revenue', icon: 'DollarSign' },
    { id: 'bidding', label: 'Bidding', icon: 'Target' },
    { id: 'geography', label: 'Geography', icon: 'MapPin' }
  ];

  const timeRanges = [
    { value: '1d', label: '24H' },
    { value: '7d', label: '7D' },
    { value: '30d', label: '30D' },
    { value: '90d', label: '90D' }
  ];

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    })?.format(amount);
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload?.length) {
      return (
        <div className="bg-card border border-border rounded-lg p-3 shadow-modal">
          <p className="text-sm font-medium text-foreground mb-2">{label}</p>
          {payload?.map((entry, index) => (
            <p key={index} className="text-sm" style={{ color: entry?.color }}>
              {entry?.name}: {entry?.name?.includes('revenue') ? formatCurrency(entry?.value) : entry?.value}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  if (!selectedCampaign) {
    return (
      <div className="h-full bg-card border-l border-border flex items-center justify-center">
        <div className="text-center">
          <Icon name="BarChart3" size={48} className="text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-foreground mb-2">Campaign Analytics</h3>
          <p className="text-sm text-muted-foreground">
            Select a campaign to view detailed analytics and performance metrics
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full bg-card border-l border-border flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-foreground">{selectedCampaign?.name}</h3>
            <p className="text-sm text-muted-foreground">Campaign Analytics</p>
          </div>
          <div className="flex items-center space-x-2">
            {timeRanges?.map((range) => (
              <button
                key={range?.value}
                onClick={() => setTimeRange(range?.value)}
                className={`px-3 py-1 text-xs rounded transition-smooth ${
                  timeRange === range?.value
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                }`}
              >
                {range?.label}
              </button>
            ))}
          </div>
        </div>

        {/* Tabs */}
        <div className="flex space-x-1">
          {tabs?.map((tab) => (
            <button
              key={tab?.id}
              onClick={() => setActiveTab(tab?.id)}
              className={`flex items-center space-x-2 px-3 py-2 text-sm rounded-lg transition-smooth ${
                activeTab === tab?.id
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted'
              }`}
            >
              <Icon name={tab?.icon} size={16} />
              <span>{tab?.label}</span>
            </button>
          ))}
        </div>
      </div>
      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4">
        {activeTab === 'performance' && (
          <div className="space-y-6">
            {/* Key Metrics */}
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-muted/30 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-muted-foreground">Total Calls</span>
                  <Icon name="Phone" size={16} className="text-accent" />
                </div>
                <div className="text-2xl font-bold text-foreground">{selectedCampaign?.callVolume}</div>
                <div className="flex items-center space-x-1 mt-1">
                  <Icon name="TrendingUp" size={12} className="text-success" />
                  <span className="text-xs text-success">+12.5%</span>
                </div>
              </div>
              <div className="p-4 bg-muted/30 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-muted-foreground">Win Rate</span>
                  <Icon name="Target" size={16} className="text-accent" />
                </div>
                <div className="text-2xl font-bold text-foreground">{selectedCampaign?.bidWinPercentage}%</div>
                <div className="flex items-center space-x-1 mt-1">
                  <Icon name="TrendingUp" size={12} className="text-success" />
                  <span className="text-xs text-success">+3.2%</span>
                </div>
              </div>
            </div>

            {/* Performance Chart */}
            <div>
              <h4 className="text-sm font-medium text-foreground mb-3">24-Hour Performance</h4>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={performanceData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                    <XAxis dataKey="time" stroke="var(--color-muted-foreground)" fontSize={12} />
                    <YAxis stroke="var(--color-muted-foreground)" fontSize={12} />
                    <Tooltip content={<CustomTooltip />} />
                    <Line
                      type="monotone"
                      dataKey="calls"
                      stroke="var(--color-primary)"
                      strokeWidth={2}
                      dot={{ fill: 'var(--color-primary)', strokeWidth: 2 }}
                    />
                    <Line
                      type="monotone"
                      dataKey="bids"
                      stroke="var(--color-accent)"
                      strokeWidth={2}
                      dot={{ fill: 'var(--color-accent)', strokeWidth: 2 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'revenue' && (
          <div className="space-y-6">
            {/* Revenue Metrics */}
            <div className="grid grid-cols-1 gap-4">
              <div className="p-4 bg-success/10 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-muted-foreground">Total Revenue</span>
                  <Icon name="DollarSign" size={16} className="text-success" />
                </div>
                <div className="text-2xl font-bold text-success">
                  {formatCurrency(selectedCampaign?.revenue)}
                </div>
                <div className="flex items-center space-x-1 mt-1">
                  <Icon name="TrendingUp" size={12} className="text-success" />
                  <span className="text-xs text-success">+18.7% vs last week</span>
                </div>
              </div>
            </div>

            {/* Revenue Trend */}
            <div>
              <h4 className="text-sm font-medium text-foreground mb-3">Revenue Trend (7 Days)</h4>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={revenueData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                    <XAxis dataKey="date" stroke="var(--color-muted-foreground)" fontSize={12} />
                    <YAxis stroke="var(--color-muted-foreground)" fontSize={12} />
                    <Tooltip content={<CustomTooltip />} />
                    <Area
                      type="monotone"
                      dataKey="revenue"
                      stroke="var(--color-success)"
                      fill="var(--color-success)"
                      fillOpacity={0.1}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'bidding' && (
          <div className="space-y-6">
            {/* Bid Metrics */}
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-muted/30 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-muted-foreground">Avg Bid Price</span>
                  <Icon name="Target" size={16} className="text-accent" />
                </div>
                <div className="text-2xl font-bold text-foreground">${selectedCampaign?.avgBidPrice}</div>
              </div>
              <div className="p-4 bg-muted/30 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-muted-foreground">Top Buyer</span>
                  <Icon name="Crown" size={16} className="text-warning" />
                </div>
                <div className="text-sm font-medium text-foreground truncate">
                  {selectedCampaign?.topBuyer}
                </div>
              </div>
            </div>

            {/* Bid Distribution */}
            <div>
              <h4 className="text-sm font-medium text-foreground mb-3">Bid Price Distribution</h4>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={bidDistribution}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                    <XAxis dataKey="range" stroke="var(--color-muted-foreground)" fontSize={12} />
                    <YAxis stroke="var(--color-muted-foreground)" fontSize={12} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="count" fill="var(--color-primary)" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'geography' && (
          <div className="space-y-6">
            {/* Geographic Distribution */}
            <div>
              <h4 className="text-sm font-medium text-foreground mb-3">Geographic Performance</h4>
              <div className="space-y-3">
                {geographicData?.map((item) => (
                  <div key={item?.state} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                        <span className="text-xs font-medium text-primary">{item?.state}</span>
                      </div>
                      <div>
                        <div className="text-sm font-medium text-foreground">{item?.calls} calls</div>
                        <div className="text-xs text-muted-foreground">{formatCurrency(item?.revenue)}</div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-medium text-foreground">{item?.percentage}%</div>
                      <div className="w-16 h-2 bg-muted rounded-full mt-1">
                        <div
                          className="h-full bg-primary rounded-full"
                          style={{ width: `${item?.percentage}%` }}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
      {/* Footer Actions */}
      <div className="p-4 border-t border-border">
        <div className="flex items-center justify-between">
          <button className="flex items-center space-x-2 text-sm text-muted-foreground hover:text-foreground transition-smooth">
            <Icon name="Download" size={16} />
            <span>Export Data</span>
          </button>
          <button className="flex items-center space-x-2 text-sm text-muted-foreground hover:text-foreground transition-smooth">
            <Icon name="Share2" size={16} />
            <span>Share Report</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default CampaignAnalyticsPanel;